import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Doughnut, Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement, Tooltip, Legend,
} from 'chart.js';
import {
  MdRestaurant, MdEvent, MdCheckCircle, MdTrendingUp, MdSubscriptions,
  MdTrendingDown, MdShowChart, MdFavorite, MdWarningAmber, MdSwapVert,
} from 'react-icons/md';
import { FaRegCreditCard } from 'react-icons/fa';
import { AppContext, useApp } from '../contexts/AppContext';
import { useProfile } from '../contexts/ProfileContext';
import { computeVizFromProfile } from '../utils/computeVizFromProfile';
import { CHART_COLORS } from '../constants/colors';
import { AGENT_BY_KEY, pollUntilReady } from '../components/insightsRender';
import { fetchInsights } from '../services/backendAPI';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

// ── Fallback static data (no profile loaded) ─────────────────────────────────
const STATIC_MONTHS         = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
const STATIC_MONTHLY_SPEND  = [1820, 2050, 1740, 1980, 2314, 1650];
const STATIC_MONTHLY_INCOME = [3200, 3200, 3200, 3450, 3200, 3200];
const STATIC_CATEGORIES    = ['Dining', 'Groceries', 'Transport', 'Shopping', 'Subscriptions', 'Other'];
const STATIC_CAT_SPEND     = [1749, 1240, 680, 920, 600, 411];
const STATIC_CHIPS = [
  { Icon: MdRestaurant,    color: '#d32f2f', label: 'Top Category',  value: 'Dining',   sub: '$1,749 / 6 mo' },
  { Icon: MdEvent,         color: '#f59e0b', label: 'Biggest Month', value: 'December', sub: '$2,314 spent'  },
  { Icon: FaRegCreditCard, color: '#1976d2', label: 'Avg / Month',   value: '$1,926',   sub: 'CC spend'      },
  { Icon: MdCheckCircle,   color: '#008a0d', label: 'No Interest',   value: 'Visa',     sub: 'Paid in full'  },
];

// Curated read-only detail sections — the "key spending attributes" surfaced
// straight from the analysis agents. Running/re-running lives on the Agents
// dashboard (🤖 button); this screen just reflects what IVA saw.
const DETAIL_KEYS = ['spendDigest', 'budgetPulse', 'anomalies', 'narrative'];

// Hold the last results so reopening the screen shows them instantly while a
// fresh fetch happens in the background. Mirrors AgentsScreen's cache.
let cachedInsights = { sessionId: null, data: null };

// Combo chart: spending as bars, income as an overlaid line. Income and spending
// are both monthly *flows*, so they share the y-axis honestly. (Chequing balance
// is a running stock — to plot that instead, swap `income` for viz.balanceData
// values and give the line dataset a second y-axis.)
function buildSpendIncomeData(months, spend, income) {
  const max = spend.length ? Math.max(...spend) : 0;
  return {
    labels: months,
    datasets: [
      {
        type: 'bar',
        label: 'Spending',
        data: spend,
        backgroundColor: spend.map(v => v === max ? CHART_COLORS[0] : `${CHART_COLORS[0]}55`),
        borderRadius: 6,
        borderSkipped: false,
        order: 2,
      },
      {
        type: 'line',
        label: 'Income',
        data: income,
        borderColor: '#008a0d',
        backgroundColor: '#008a0d',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#008a0d',
        order: 1,
      },
    ],
  };
}

function buildDoughnutData(labels, values) {
  return {
    labels,
    datasets: [{
      data: values,
      backgroundColor: CHART_COLORS.slice(0, labels.length),
      borderColor: 'transparent',
      borderWidth: 0,
    }],
  };
}

const BAR_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      align: 'end',
      labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, font: { size: 11 }, color: '#999' },
    },
    tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: $${ctx.raw.toLocaleString()}` } },
  },
  scales: {
    x: { ticks: { color: '#999', font: { size: 11 } }, grid: { display: false } },
    y: {
      ticks: { color: '#999', font: { size: 10 }, callback: v => `$${(v / 1000).toFixed(v >= 1000 ? 1 : 0)}k` },
      grid: { color: 'rgba(128,128,128,0.1)' },
      border: { display: false },
    },
  },
};

const DONUT_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '68%',
  plugins: {
    legend: { display: false },
    tooltip: { callbacks: { label: ctx => ` ${ctx.label}: $${ctx.raw.toLocaleString()}` } },
  },
};

// Build the four headline chips from live agent output: top category (+MoM),
// biggest spending mover, financial-health score, and the unusual-charge count.
// Returns null when the agents haven't produced enough to fill them.
function deriveAgentChips(ins) {
  if (!ins?.ready) return null;
  const chips = [];

  const topCat = ins.spendDigest?.byCategory?.[0];
  if (topCat) {
    chips.push({
      Icon: MdRestaurant, color: '#d32f2f', label: 'Top Category',
      value: topCat.category,
      sub: `$${topCat.amount.toLocaleString()}${topCat.pct != null ? ` • ${topCat.pct}%` : ''}`,
    });
  }

  // Biggest mover: prefer budgetPulse, else the largest MoM swing in the digest.
  const pulse = (ins.budgetPulse || [])
    .slice()
    .sort((a, b) => Math.abs(b.changePct) - Math.abs(a.changePct))[0];
  const digestMover = (ins.spendDigest?.byCategory || [])
    .filter(c => c.changePct != null)
    .slice()
    .sort((a, b) => Math.abs(b.changePct) - Math.abs(a.changePct))[0];
  const mover = pulse
    ? { category: pulse.category, up: pulse.direction === 'up', pct: Math.abs(pulse.changePct) }
    : digestMover
      ? { category: digestMover.category, up: digestMover.changePct > 0, pct: Math.abs(digestMover.changePct) }
      : null;
  if (mover) {
    chips.push({
      Icon: mover.up ? MdTrendingUp : MdTrendingDown,
      color: mover.up ? '#c0392b' : '#1b7a3d',
      label: 'Biggest Shift',
      value: mover.category,
      sub: `${mover.up ? '↑' : '↓'} ${mover.pct}% vs usual`,
    });
  }

  const h = ins.healthScore;
  if (h?.maxScore) {
    const ratio = h.score / h.maxScore;
    chips.push({
      Icon: MdFavorite,
      color: ratio >= 0.66 ? '#008a0d' : ratio >= 0.4 ? '#d99100' : '#c0392b',
      label: 'Health Score',
      value: `${h.score} / ${h.maxScore}`,
      sub: ratio >= 0.66 ? 'Healthy' : ratio >= 0.4 ? 'Coping' : 'Strained',
    });
  }

  const anomalies = ins.anomalies || [];
  chips.push({
    Icon: anomalies.length ? MdWarningAmber : MdCheckCircle,
    color: anomalies.length ? '#f59e0b' : '#008a0d',
    label: 'Unusual Charges',
    value: anomalies.length ? String(anomalies.length) : 'None',
    sub: anomalies.length ? 'flagged this period' : 'nothing unusual',
  });

  return chips.length ? chips.slice(0, 4) : null;
}

const InsightsScreen = ({ onNavigate }) => {
  const { sessionId } = useContext(AppContext);
  const { simDate } = useApp();
  const { loadedProfileData } = useProfile();

  const [insights, setInsights] = useState(() =>
    cachedInsights.sessionId === sessionId ? cachedInsights.data : null
  );
  const [loading, setLoading] = useState(false);

  const store = useCallback((data, sid) => {
    cachedInsights = { sessionId: sid, data };
    setInsights(data);
  }, []);

  // On open, SHOW the existing agent results (the background run already fires on
  // profile load). Catch an initial run still in flight with a short poll — never
  // a full re-run; running is the Agents dashboard's job.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      let data = await fetchInsights(sessionId);
      if (!data?.ready && !cancelled) data = await pollUntilReady(sessionId, 8, 700);
      if (!cancelled && data?.ready) store(data, sessionId);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [sessionId, store]);

  const viz = useMemo(() => {
    if (!loadedProfileData?.profile) return null;
    return computeVizFromProfile(loadedProfileData.profile, loadedProfileData.source);
  }, [loadedProfileData]);

  // ── Derive chart data (profile-derived; agents don't drive the charts) ───────
  const months       = viz ? viz.ccMonthlyData.map(d => d.month) : STATIC_MONTHS;
  const monthlySpend = viz
    ? viz.ccMonthlyData.map(d => viz.allCCCats.reduce((s, k) => s + (d[k] || 0), 0))
    : STATIC_MONTHLY_SPEND;
  const monthlyIncome = viz
    ? viz.incomeData.map(d => viz.incomeCatKeys.reduce((s, k) => s + (d[k] || 0), 0))
    : STATIC_MONTHLY_INCOME;

  const catLabels = viz ? viz.donutData.map(d => d.name) : STATIC_CATEGORIES;
  const catValues = viz ? viz.donutData.map(d => d.value) : STATIC_CAT_SPEND;

  const barData     = buildSpendIncomeData(months, monthlySpend, monthlyIncome);
  const donutData   = buildDoughnutData(catLabels, catValues);

  // ── Chips: prefer live agent output, then viz-derived, then static ───────────
  const vizChips = useMemo(() => {
    if (!viz) return STATIC_CHIPS;

    const topCat = viz.donutData?.[0];
    const hasSpend = monthlySpend.length > 0 && monthlySpend.some(v => v > 0);
    const maxVal = hasSpend ? Math.max(...monthlySpend) : 0;
    const maxIdx = hasSpend ? monthlySpend.indexOf(maxVal) : -1;
    const balanceTrend = (viz.balanceData || []).filter(d => d.Balance != null);
    const trendUp = balanceTrend.length >= 2
      ? balanceTrend[balanceTrend.length - 1].Balance >= balanceTrend[0].Balance
      : true;
    const avgCC = viz.stats?.avgMonthlyCC ?? 0;

    return [
      {
        Icon: MdRestaurant,
        color: '#d32f2f',
        label: 'Top Category',
        value: topCat?.name || '—',
        sub: topCat ? `$${topCat.value.toLocaleString()} total` : '—',
      },
      {
        Icon: MdEvent,
        color: '#f59e0b',
        label: 'Biggest Month',
        value: maxIdx >= 0 ? (months[maxIdx] || '—') : '—',
        sub: maxIdx >= 0 ? `$${monthlySpend[maxIdx].toLocaleString()} spent` : '—',
      },
      {
        Icon: MdShowChart,
        color: '#1976d2',
        label: 'Avg / Month',
        value: `$${avgCC.toLocaleString()}`,
        sub: 'CC spend',
      },
      {
        Icon: trendUp ? MdTrendingUp : MdTrendingDown,
        color: trendUp ? '#008a0d' : '#d32f2f',
        label: 'Balance Trend',
        value: trendUp ? 'Growing' : 'Declining',
        sub: viz.periodLabel || '—',
      },
    ];
  }, [viz, months, monthlySpend]);

  const agentChips = useMemo(() => deriveAgentChips(insights), [insights]);
  const chips = agentChips || vizChips;
  const chipsAreLive = !!agentChips;

  // ── Curated agent detail sections ────────────────────────────────────────────
  const detailSections = useMemo(() => {
    if (!insights?.ready) return [];
    return DETAIL_KEYS
      .map(key => {
        const agent = AGENT_BY_KEY[key];
        if (!agent) return null;
        const body = agent.render(insights);
        const hasBody = body != null && (!Array.isArray(body) || body.length > 0);
        return hasBody ? { agent, body } : null;
      })
      .filter(Boolean);
  }, [insights]);

  const periodLabel = viz?.periodLabel || 'Last 6 months';
  const profileName = loadedProfileData?.name;
  const showAgentLoading = loading && !insights?.ready && !!sessionId;

  return (
    <div className="screen insights-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-sm)' }}>
          <div className="bh-flow-hero-left">
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <h1 className="bh-flow-title">Insights</h1>
              {profileName && (
                <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', fontWeight: 500, marginTop: 2 }}>{profileName}</div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => onNavigate && onNavigate('finances')}
              title="Open your Finances / cashflow"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'rgba(0,0,0,0.08)', color: 'var(--bh-black, #111)', border: 'none',
                borderRadius: 'var(--radius-full, 999px)', padding: '8px 16px', fontWeight: 700, cursor: 'pointer',
              }}
            >
              💵 Finances
            </button>
            <button
              onClick={() => onNavigate && onNavigate('agents')}
              title="Open the AI agents dashboard"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'var(--td-green, #2FBF00)', color: '#fff', border: 'none',
                borderRadius: 'var(--radius-full, 999px)', padding: '8px 16px', fontWeight: 700, cursor: 'pointer',
              }}
            >
              🤖 Agents
            </button>
          </div>
        </div>
      </header>

      <div className="scroll-content gray-bg ins-scroll">

        {simDate && (
          <div style={{
            fontSize: 9, fontFamily: 'monospace', color: '#f59e0b', letterSpacing: 0.8,
            background: '#f59e0b14', border: '1px solid #f59e0b44',
            borderRadius: 6, padding: '4px 10px', textAlign: 'center', marginBottom: 6,
          }}>
            ⚠ Insights reflect profile load — not the simulated date
          </div>
        )}

        {chipsAreLive && (
          <div style={{ fontSize: 12, color: 'var(--text-light)', textAlign: 'center', marginBottom: 2 }}>
            live from IVA agents
          </div>
        )}

        {/* Insight chips — agent-derived when available */}
        <div className="ins-chips-grid">
          {chips.map(({ Icon, color, label, value, sub }) => (
            <div key={label} className="ins-chip">
              <div className="ins-chip-top">
                <div className="ins-chip-icon" style={{ background: `${color}18`, color }}>
                  <Icon size={18} />
                </div>
                <div className="ins-chip-label">{label}</div>
              </div>
              <div className="ins-chip-value">{value}</div>
              <div className="ins-chip-sub">{sub}</div>
            </div>
          ))}
        </div>

        {showAgentLoading && (
          <div className="ins-card" style={{ color: 'var(--text-light)', textAlign: 'center', fontSize: 13 }}>
            <MdSwapVert size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            IVA is analysing your spending…
          </div>
        )}

        {/* Monthly spend bar */}
        <div className="ins-card">
          <div className="ins-card-header">
            <MdTrendingUp size={15} style={{ color: CHART_COLORS[0] }} />
            <span className="ins-card-title">Spending vs. Income</span>
            <span className="ins-card-sub">{periodLabel}</span>
          </div>
          <div style={{ height: 160, marginTop: 8 }}>
            <Chart type="bar" data={barData} options={BAR_OPTS} />
          </div>
        </div>

        {/* Doughnut + legend */}
        <div className="ins-card">
          <div className="ins-card-header">
            <MdSubscriptions size={15} style={{ color: CHART_COLORS[1] }} />
            <span className="ins-card-title">Spend by Category</span>
            <span className="ins-card-sub">All time</span>
          </div>
          <div className="ins-donut-row">
            <div style={{ width: 130, height: 130, flexShrink: 0 }}>
              <Doughnut data={donutData} options={DONUT_OPTS} />
            </div>
            <div className="ins-legend">
              {catLabels.map((cat, i) => (
                <div key={cat} className="ins-legend-row">
                  <span className="ins-legend-dot" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="ins-legend-label">{cat}</span>
                  <span className="ins-legend-val">${catValues[i].toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Curated agent detail — what IVA actually saw */}
        {detailSections.map(({ agent, body }) => (
          <div key={agent.key} className="ins-card">
            <div className="ins-card-header">
              <span style={{ fontSize: '1rem' }} aria-hidden>{agent.emoji}</span>
              <span className="ins-card-title">{agent.label}</span>
              <span className="ins-card-sub">{agent.blurb}</span>
            </div>
            <div style={{ marginTop: 8 }}>{body}</div>
          </div>
        ))}

      </div>
    </div>
  );
};

export default InsightsScreen;
